import { Modal, Button, Table, Spin, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useEffect, useMemo, useRef } from 'react';
import HeadingTypography from 'src/app/components/shared/Typography/Heading';
import TextTypography from 'src/app/components/shared/Typography/Text';
import { useExistingLead } from 'src/app/hooks/useGetExistingLead';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

interface IViewPlanModal {
    visible: string;
    onClose: () => void;
}

export const ViewPlanModal = ({ visible, onClose }: IViewPlanModal) => {
    const { fetchExistingLead, isLoading, data } = useExistingLead(visible, false);
    const modalContentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        fetchExistingLead();
    }, []);

    const tableData = useMemo(() => {
        return data?.products?.map((product, index) => {
            let totalPrice = 0;
            const retail_price = Number(product.retail_price) ?? 0;
            const selectedInstallment = product.instalment_plan;
            if (selectedInstallment?.cycle) {
                const aprPercentage = (product.apr ?? 0) * (selectedInstallment.cycle ?? 0);

                const aprAmount = retail_price * (aprPercentage / 100);

                let totalLoanAmount = Number(aprAmount + retail_price);

                if (product.service_charges) {
                    totalLoanAmount += 400;
                }
                if (product.delivery_charges) {
                    totalLoanAmount += 600;
                }
                if (product.locking_charges) {
                    totalLoanAmount += Number(product.locking_charges) ?? 0;
                }

                totalPrice = totalLoanAmount;
                if (Number(product.down_payment)) {
                    totalPrice += Number(product.down_payment);
                }
                if (Number(product.token_amount)) {
                    totalPrice += Number(product.token_amount);
                }
            }

            return {
                key: index,
                product_name: product.product_name,
                product_type: product.product_type.name,
                brand: product.brand.name,
                product_description: product.product_description,
                quantity: product.quantity,
                instalment_plan: product.instalment_plan.name,
                down_payment: product.down_payment,
                total_amount: totalPrice,
            };
        });
    }, [data]);
    const columns: ColumnsType<any> = [
        {
            title: <TextTypography fontSize={12}>Product Name</TextTypography>,
            dataIndex: 'product_name',
            key: 'product_name',
            ellipsis: true,
            render: (_: string) => (
                <Tooltip title={_}>
                    <TextTypography fontSize={12} classNames="cursor-pointer flex h-[40px] truncate items-center w-[80px]">
                        {_}
                    </TextTypography>
                </Tooltip>
            ),
        },
        {
            title: <TextTypography fontSize={12}>Product Type</TextTypography>,
            dataIndex: 'product_type',
            key: 'product_type',
            ellipsis: true,
            render: (_: string) => (
                <Tooltip title={_}>
                    <TextTypography fontSize={12} classNames="cursor-pointer flex h-[40px] truncate items-center w-[80px]">
                        {_}
                    </TextTypography>
                </Tooltip>
            ),
        },
        {
            title: <TextTypography fontSize={12}>Brand</TextTypography>,
            dataIndex: 'brand',
            key: 'brand',
            ellipsis: true,
            render: (_: string) => (
                <Tooltip title={_}>
                    <TextTypography fontSize={12} classNames="cursor-pointer flex h-[40px] truncate items-center w-[80px]">
                        {_}
                    </TextTypography>
                </Tooltip>
            ),
        },
        {
            title: <TextTypography fontSize={12}>Product Name</TextTypography>,
            dataIndex: 'product_name',
            key: 'product_name',
            ellipsis: true,
            render: (_: string) => (
                <Tooltip title={_}>
                    <TextTypography fontSize={12} classNames="cursor-pointer flex h-[40px] truncate items-center w-[80px]">
                        {_}
                    </TextTypography>
                </Tooltip>
            ),
        },
        {
            title: <TextTypography fontSize={12}>Product description</TextTypography>,
            dataIndex: 'product_description',
            key: 'product_description',
            ellipsis: true,
            render: (_: string) => (
                <Tooltip title={_}>
                    <TextTypography fontSize={12} classNames="cursor-pointer flex h-[40px] truncate items-center w-[80px]">
                        {_}
                    </TextTypography>
                </Tooltip>
            ),
        },
        {
            title: <TextTypography fontSize={12}>Quantity</TextTypography>,
            dataIndex: 'quantity',
            key: 'quantity',
            ellipsis: true,
            render: (_: string) => (
                <Tooltip title={_}>
                    <TextTypography fontSize={12} classNames="cursor-pointer flex h-[40px] truncate items-center w-[80px]">
                        {_}
                    </TextTypography>
                </Tooltip>
            ),
        },
        {
            title: <TextTypography fontSize={12}>installment Plan</TextTypography>,
            dataIndex: 'instalment_plan',
            key: 'instalment_plan',
            ellipsis: true,
            render: (_: string) => (
                <Tooltip title={_}>
                    <TextTypography fontSize={12} classNames="cursor-pointer flex h-[40px] truncate items-center w-[80px]">
                        {_}
                    </TextTypography>
                </Tooltip>
            ),
        },
        {
            title: <TextTypography fontSize={12}>Down Payment</TextTypography>,
            dataIndex: 'down_payment',
            key: 'down_payment',
            ellipsis: true,
            render: (_: string) => (
                <Tooltip title={_}>
                    <TextTypography fontSize={12} classNames="cursor-pointer flex h-[40px] truncate items-center w-[80px]">
                        {_}
                    </TextTypography>
                </Tooltip>
            ),
        },
        {
            title: <TextTypography fontSize={12}>Total Amount</TextTypography>,
            dataIndex: 'total_amount',
            key: 'total_amount',
            ellipsis: true,
            render: (_: string) => (
                <Tooltip title={_}>
                    <TextTypography fontSize={12} classNames="cursor-pointer flex h-[40px] truncate items-center w-[80px]">
                        {_}
                    </TextTypography>
                </Tooltip>
            ),
        },
    ];

  const handleCopyAsImage = async () => {
    if (modalContentRef.current) {
      try {
        const canvas = await html2canvas(modalContentRef.current, {
          scale: 2, // Increase resolution
          useCORS: true,
          backgroundColor: '#ffffff',
        });
        canvas.toBlob((blob) => {
          if (blob) {
            navigator.clipboard.write([
              new ClipboardItem({ 'image/png': blob }),
            ]);
            alert('Image copied to clipboard!');
          }
        }, 'image/png');
      } catch (error) {
        console.error('Error copying image:', error);
        alert('Failed to copy image.');
      }
    }
  };

  const handleDownloadAsPDF = async () => {

    if (modalContentRef.current) {
      try {
        const canvas = await html2canvas(modalContentRef.current, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
        });
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF({
          orientation: 'landscape',
          unit: 'px',
          format: [canvas.width, canvas.height],
        });
        pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
        pdf.save('plan-details.pdf');
      } catch (error) {
        console.error('Error generating PDF:', error);
        alert('Failed to download PDF.');
      }
    }
  };

    return (
        <Modal open={visible ? true : false} onCancel={onClose} footer={null} width={1100} className="p-6">
            <div >
                {isLoading ? (
                    <div className="spinner-container flex justify-center p-[20px]">
                        <Spin size="large" />
                    </div>
                ) : (
                    <>
                      <div ref={modalContentRef}>
                        <div className="text-start mb-4">
                            <HeadingTypography classNames=" font-semibold text-[#2E2A2A]" fontSize={22}>
                                View Plan Details
                            </HeadingTypography>
                            <div className="w-[100%] h-[2px] bg-[#BD273B] mt-5"></div>
                            <div className="flex justify-center mt-5">
                                <img src="../../../../../../../assets/rasayi-logo.png" style={{ width: 80 }} alt="logo" />
                            </div>
                        </div>
                        <div className="flex w-[100%]">
                            <div className="w-[50%]">
                                <div className="flex gap-[50px]">
                                    <HeadingTypography classNames="font-medium" fontSize={14}>
                                        Full Name
                                    </HeadingTypography>
                                    <TextTypography fontSize={14}>{data?.first_name ?? '' + data?.last_name ?? ''}</TextTypography>
                                </div>
                                <div className="flex gap-[15px]">
                                    <HeadingTypography classNames="font-medium" fontSize={14}>
                                        Phone Number
                                    </HeadingTypography>
                                    <TextTypography fontSize={14}>{data?.phone ?? ''}</TextTypography>
                                </div>
                                <div className="flex gap-[77px]">
                                    <HeadingTypography classNames="font-medium" fontSize={14}>
                                        CNIC
                                    </HeadingTypography>
                                    <TextTypography fontSize={14}>{data?.cnic_number ?? ''}</TextTypography>
                                </div>
                            </div>
                            <div className="w-[50%]">
                                <div className="flex flex-col  gap-[15px]">
                                    <HeadingTypography classNames="font-medium text-start" fontSize={14}>
                                        Address
                                    </HeadingTypography>
                                    <TextTypography classNames="text-start" fontSize={14}>
                                        Lahore Division, Lums, Street 7 Sector U Phase 2, Rehman Villas, Chung Khurad 54792, Pakistan
                                    </TextTypography>
                                </div>
                            </div>
                        </div>
                        <div className="w-[100%] flex justify-between mt-5">
                            <div className="flex   gap-[15px]">
                                <HeadingTypography classNames="font-medium text-start" fontSize={14}>
                                    User ID
                                </HeadingTypography>
                                <TextTypography classNames="text-start" fontSize={14}>
                                    {data?.id.slice(-9)}
                                </TextTypography>
                            </div>
                            <div className="flex  gap-[15px]">
                                <HeadingTypography classNames="font-medium text-start" fontSize={14}>
                                    Date
                                </HeadingTypography>
                                <TextTypography classNames="text-start" fontSize={14}>
                                    {data?.created_at?.toString()?.split('T')?.[0] || ''}
                                </TextTypography>
                            </div>
                        </div>


                        <Table className='mt-5' columns={columns} dataSource={tableData} rowKey={'id'} size="small" scroll={{ x: true }} pagination={false}/>


                        <p className="text-red-500 text-sm text-center mt-10">The quoted price is valid for today only and is subject to change based on availability.</p>


                        <div className="w-[100%] h-[2px] bg-[#BD273B] mt-5"></div>
                      </div>

                        <div className="flex justify-end mt-4 gap-2">
                          <Button className="bg-gray-200" onClick={handleCopyAsImage}>
                            Copy
                          </Button>
                          <Button type="primary" onClick={handleDownloadAsPDF}>
                            Download
                          </Button>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};
